import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { UserContext } from '../../Context/UserContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { FaArrowLeft, FaGift, FaSave, FaEdit, FaUsers, FaMapMarkerAlt, FaTimes, FaClock, FaCalendarAlt } from 'react-icons/fa';
import { checkIsGlobalAdmin } from '../../utils/authUtils';
import './CreateCustomerGroup.css';
import 'react-toastify/dist/ReactToastify.css';

const CreateRewardPage = () => {
  const { baseUrl, getHeaders, user } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const editReward = location.state?.reward;
  const [loading, setLoading] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState(editReward?.company_names || (editReward?.company_name ? [editReward.company_name] : []));
  const [companyOptions, setCompanyOptions] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState(editReward?.branch_names || (editReward?.branch_name ? [editReward.branch_name] : []));
  const [availableBranches, setAvailableBranches] = useState([]);
  const [isGroupAdmin, setIsGroupAdmin] = useState(false);

  const [formData, setFormData] = useState({
    reward_name: editReward?.reward_name || '',
    applies_to: editReward?.applies_to || 'generally',
    group_name: editReward?.group_name || '',
    subgroup_name: editReward?.subgroup_name || '',
    product_name: editReward?.product_name || '',
    target_id: editReward?.target_id || '',
    amount_spent: editReward?.amount_spent || '',
    points_awarded: editReward?.points_awarded || '',
    is_active: editReward?.is_active ?? true,
    has_time_limit: editReward?.has_time_limit || false,
    start_date: editReward?.start_date || '',
    end_date: editReward?.end_date || '',
    start_time: editReward?.start_time || '',
    end_time: editReward?.end_time || '',
    days_of_week: editReward?.days_of_week || ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
  });

  const [groups, setGroups] = useState([]);
  const [items, setItems] = useState([]);
  const [offers, setOffers] = useState([]);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    const userObj = userStr ? JSON.parse(userStr) : {};
    setIsGroupAdmin(checkIsGlobalAdmin(userObj));
    fetchCompanies(userObj);
  }, [baseUrl]);

  const fetchCompanies = async (userObj) => {
    try {
      let comps = [];
      if (userObj.companies && userObj.companies.length > 0) {
        comps = userObj.companies.map(c => typeof c === 'object' && c !== null ? (c.company_name || c.company || c.name || '') : c).filter(c => c && typeof c === 'string');
      } else if (userObj.company_name || userObj.company) {
        comps = [userObj.company_name || userObj.company];
      } else {
        const res = await axios.get(`${baseUrl}/api/company-details`, { headers: getHeaders() });
        const details = res.data.companyDetails || [];
        comps = details.map(d => d.restaurantName).filter(n => n);
      }
      const finalComps = comps.length > 0 ? [...new Set(comps)] : [];
      setCompanyOptions(finalComps);

      const activeContext = localStorage.getItem('active_company');
      if (selectedCompanies.length === 0 && finalComps.length > 0) {
        if (activeContext && activeContext !== 'All' && finalComps.includes(activeContext)) {
          setSelectedCompanies([activeContext]);
        } else if (activeContext === 'All') {
          setSelectedCompanies(finalComps);
        } else if (!activeContext && finalComps.length === 1) {
          setSelectedCompanies([finalComps[0]]);
        }
      }
    } catch (e) { 
      setCompanyOptions([]);
    }
  };

  const fetchBranches = async () => {
    if (selectedCompanies.length === 0) {
      setAvailableBranches([]);
      return;
    }
    try {
      const branchPromises = selectedCompanies.map(comp =>
        axios.get(`${baseUrl}/api/branches?company_name=${encodeURIComponent(comp)}`, {
          headers: { ...getHeaders(), 'X-Company-Name': comp }
        })
      );
      const results = await Promise.all(branchPromises);
      const allBranches = results.flatMap(res =>
        (res.data || []).map(b => typeof b === 'string' ? b : (b.branch_name || b.branch || b.name))
      ).filter(b => b);
      setAvailableBranches([...new Set(allBranches)]);
    } catch (err) {
      console.error("Failed to fetch branches:", err);
    }
  };

  useEffect(() => {
    if (selectedCompanies.length > 0) fetchBranches();
    else setAvailableBranches([]);
  }, [selectedCompanies]);

  useEffect(() => {
    fetchData();
  }, [selectedCompanies, selectedBranches]);

  const fetchData = async () => {
    try {
      const active_company = selectedCompanies.length > 0 ? selectedCompanies[0] : (localStorage.getItem('active_company') || user?.company_name);
      const active_branch = selectedBranches.length > 0 ? selectedBranches[0] : (localStorage.getItem('active_branch') || user?.branch_name);
      const headers = getHeaders(active_branch, active_company);

      const [groupRes, itemsRes, offersRes] = await Promise.allSettled([
        axios.get(`${baseUrl}/api/item-groups`, { headers }),
        axios.get(`${baseUrl}/api/items`, { headers }),
        axios.get(`${baseUrl}/api/combo-offer`, { headers })
      ]);

      if (groupRes.status === 'fulfilled') setGroups(groupRes.value.data || []);
      if (itemsRes.status === 'fulfilled') setItems(itemsRes.value.data || []);
      if (offersRes.status === 'fulfilled') setOffers(offersRes.value.data || []);

    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const activeBranchLocal = (localStorage.getItem('active_branch') || 'All Branches').trim();
  const isSpecificBranchActive = activeBranchLocal.toLowerCase() !== 'all branches' && activeBranchLocal.toLowerCase() !== 'all' && activeBranchLocal !== '';
  const showCompanyAssignBtn = !isSpecificBranchActive && isGroupAdmin && companyOptions.length > 0;
  const showBranchAssignBtn = !isSpecificBranchActive && selectedCompanies.length > 0 && availableBranches.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let finalBranches = selectedBranches;
    let finalCompanies = selectedCompanies;
    const activeBranchLoc = (localStorage.getItem('active_branch') || 'All Branches').trim();
    const activeCompLoc = (localStorage.getItem('active_company') || 'All Companies').trim();
    const isSpecBranch = activeBranchLoc.toLowerCase() !== 'all branches' && activeBranchLoc.toLowerCase() !== 'all' && activeBranchLoc !== '';
    const isSpecComp = activeCompLoc.toLowerCase() !== 'all companies' && activeCompLoc.toLowerCase() !== 'all' && activeCompLoc !== '';

    if (isSpecBranch && finalBranches.length === 0) finalBranches = [activeBranchLoc];
    if (isSpecComp && finalCompanies.length === 0) finalCompanies = [activeCompLoc];

    if (!showAssignmentModal) {
      if (showCompanyAssignBtn || showBranchAssignBtn) {
        setShowAssignmentModal(true);
        return;
      }
    } else {
      if (showCompanyAssignBtn && finalCompanies.length === 0) {
        toast.warn("Company selection is required");
        return;
      }
    }

    setLoading(true);

    try {
      const active_company = localStorage.getItem('active_company') || user?.company_name;
      const active_branch = localStorage.getItem('active_branch') || user?.branch_name;

      let finCompanies = finalCompanies.length > 0 ? finalCompanies : (active_company && active_company !== 'All' && active_company !== 'All Companies' ? [active_company] : []);
      let finBranches = finalBranches.length > 0 ? finalBranches : (active_branch && active_branch !== 'All Branches' && active_branch !== 'All' ? [active_branch] : []);

      const payload = {
        ...formData,
        amount_spent: parseFloat(formData.amount_spent),
        points_awarded: parseFloat(formData.points_awarded),
        company_name: finCompanies[0] || "",
        company_names: finCompanies,
        branch_name: finBranches[0] || "",
        branch_names: finBranches
      };

      if (formData.applies_to === 'offer') {
        const selectedOffer = offers.find(o => o._id === formData.target_id);
        if (selectedOffer) {
          payload.product_name = selectedOffer.description || selectedOffer.combo_name || selectedOffer.name1 || 'Combo Offer';
        }
      }

      if (editReward && (editReward.id || editReward._id)) {
        await axios.put(`${baseUrl}/api/reward-rules/${editReward.id || editReward._id}`, payload, {
          headers: getHeaders(active_branch, active_company)
        });
        toast.success("Reward Rule updated successfully!");
      } else {
        await axios.post(`${baseUrl}/api/reward-rules`, payload, {
          headers: getHeaders(active_branch, active_company)
        });
        toast.success("Reward Rule created successfully!");
      }
      
      setShowAssignmentModal(false);
      setTimeout(() => navigate('/rewards'), 1500);
    } catch (error) {
      console.error("Error saving reward:", error);
      toast.error("Failed to save reward rule.");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="create-customer-container customer-group-page main-customer-page">
      <ToastContainer />
      
      {/* Header section */}
      <div className="customer-header-section">
        <div className="header-left">
          <button type="button" onClick={() => navigate(-1)} className="header-back-btn">
            <FaArrowLeft /> Back
          </button>
        </div>

        <div className="header-center">
          <h1>{editReward ? 'Edit' : 'Create'} Reward Rule</h1>
        </div>

        <div className="header-right">
          <div className="header-actions">
            {showCompanyAssignBtn && (
              <button 
                type="button"
                className="customize-btn" 
                onClick={() => setShowAssignmentModal(true)}
                style={{
                  background: '#eff6ff', color: '#3b82f6', border: '1.5px solid #dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '0 24px', height: '48px', minWidth: '140px', borderRadius: '14px', fontWeight: '700', fontSize: '15px', cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.1)'
                }}
              >
                <FaUsers /> Company
              </button>
            )}
            {showBranchAssignBtn && (
              <button 
                type="button"
                className="customize-btn branch-btn" 
                onClick={() => setShowAssignmentModal(true)}
                style={{
                  background: '#fff7ed', color: '#ea580c', border: '1.5px solid #ffedd5', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '0 24px', height: '48px', minWidth: '140px', borderRadius: '14px', fontWeight: '700', fontSize: '15px', cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 4px 6px -1px rgba(234, 88, 12, 0.1)'
                }}
              >
                <FaMapMarkerAlt /> Branches
              </button>
            )}
            <button className="save-btn" onClick={handleSubmit} disabled={loading}>
              {loading ? "Saving..." : (editReward ? <><FaEdit /> Update Rule</> : <><FaSave /> Save Rule</>)}
            </button>
          </div>
        </div>
      </div>
      <div className="header-divider"></div>

      {/* Body scroll view */}
      <div className="customer-scroll-body">
        <div className="form-sections-wrapper">
          <div className="form-section-card theme-blue">
            <div className="section-header">
              <div className="section-icon"><FaGift /></div>
              <h2>Reward Details</h2>
            </div>

            <div className="form-grid">

              <div className="form-group">
                <label>Reward Name <span className="required">*</span></label>
                <input
                  type="text"
                  name="reward_name"
                  value={formData.reward_name}
                  onChange={handleChange}
                  required
                  placeholder="e.g. Weekend Bonus"
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc' }}
                />
              </div>

              <div className="form-group">
                <label>Applies To <span className="required">*</span></label>
                <select
                  name="applies_to"
                  value={formData.applies_to}
                  onChange={handleChange}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc' }}
                >
                  <option value="generally">All Items (Generally / Entire Cart)</option>
                  <option value="group">Specific Item Group</option>
                  <option value="subgroup">Specific Subgroup</option>
                  <option value="product">Specific Product</option>
                  <option value="offer">Specific Offer</option>
                </select>
                {formData.applies_to === 'generally' && (
                  <p style={{ fontSize: '13px', color: '#10b981', marginTop: '6px', fontWeight: '600' }}>
                    ✨ Applies across ALL menu items and cart purchases! Every item purchased earns reward points based on the spend ratio below.
                  </p>
                )}
              </div>

              {formData.applies_to === 'group' && (
                <div className="form-group">
                  <label>Select Group <span className="required">*</span></label>
                  <select
                    name="group_name"
                    value={formData.group_name}
                    onChange={handleChange}
                    required
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc' }}
                  >
                    <option value="">-- Select Group --</option>
                    {groups.map((g, i) => {
                      const grpName = g.group_name || g.name || g.item_group || g.ITEM_GROUP || "Unknown Group";
                      return <option key={i} value={grpName}>{grpName}</option>;
                    })}
                  </select>
                </div>
              )}

              {formData.applies_to === 'subgroup' && (
                <div className="form-group">
                  <label>Subgroup Name <span className="required">*</span></label>
                  <input
                    type="text"
                    name="subgroup_name"
                    value={formData.subgroup_name}
                    onChange={handleChange}
                    required
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc' }}
                  />
                </div>
              )}

              {formData.applies_to === 'product' && (
                <div className="form-group">
                  <label>Select Product <span className="required">*</span></label>
                  <select
                    name="product_name"
                    value={formData.product_name}
                    onChange={handleChange}
                    required
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc' }}
                  >
                    <option value="">-- Select Product --</option>
                    {items.map((it, i) => (
                      <option key={i} value={it.item_name}>{it.item_name}</option>
                    ))}
                  </select>
                  {formData.product_name && items.find(it => it.item_name === formData.product_name) && (
                    <div style={{ marginTop: '8px', fontSize: '14px', color: '#604BE8', fontWeight: '600', padding: '8px 12px', background: '#f5f4ff', borderRadius: '6px', border: '1px dashed #604BE8' }}>
                      Price: ₹{items.find(it => it.item_name === formData.product_name).price_list_rate || items.find(it => it.item_name === formData.product_name).basePrice || items.find(it => it.item_name === formData.product_name).price || 0}
                    </div>
                  )}
                </div>
              )}

              {formData.applies_to === 'offer' && (
                <div className="form-group">
                  <label>Select Offer <span className="required">*</span></label>
                  <select
                    name="target_id"
                    value={formData.target_id || ''}
                    onChange={handleChange}
                    required
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc' }}
                  >
                    <option value="">-- Select Combo Offer --</option>
                    {offers.map((off, i) => (
                      <option key={i} value={off._id}>{off.description || off.combo_name || off.name1 || `Offer ${i + 1}`}</option>
                    ))}
                  </select>
                  {formData.target_id && offers.find(o => o._id === formData.target_id) && (
                    <div style={{ marginTop: '8px', fontSize: '14px', color: '#604BE8', fontWeight: '600', padding: '8px 12px', background: '#f5f4ff', borderRadius: '6px', border: '1px dashed #604BE8' }}>
                      Offer Price: {offers.find(o => o._id === formData.target_id).offer_price || offers.find(o => o._id === formData.target_id).total_price || 0}
                    </div>
                  )}
                </div>
              )}

              <div className="form-group">
                <label>Amount Spent <span className="required">*</span></label>
                <input
                  type="number"
                  name="amount_spent"
                  value={formData.amount_spent}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  placeholder="e.g. 100"
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc' }}
                />
              </div>

              <div className="form-group">
                <label>Points Awarded <span className="required">*</span></label>
                <input
                  type="number"
                  name="points_awarded"
                  value={formData.points_awarded}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.1"
                  placeholder="e.g. 10"
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc' }}
                />
              </div>

              <div className="form-group" style={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}>
                <div className="form-check form-switch" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    style={{ width: '40px', height: '20px', cursor: 'pointer', margin: 0 }}
                  />
                  <label style={{ margin: 0, fontWeight: 'bold' }}>Active Rule</label>
                </div>
              </div>
            </div>
          </div>

          {/* Date & Time Scheduling Section */}
          <div className="form-section-card" style={{ marginTop: '24px', background: '#ffffff', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', overflow: 'hidden' }}>
            <div className="section-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #f1f5f9', background: '#faf5ff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div className="section-icon" style={{ background: '#9333ea', color: '#fff', width: '36px', height: '36px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FaClock />
                </div>
                <div>
                  <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#581c87', margin: 0 }}>Schedule & Time Validity</h2>
                  <p style={{ margin: 0, fontSize: '12px', color: '#7e22ce' }}>Set specific dates, hours, or days when this reward rule applies</p>
                </div>
              </div>
              <div className="form-check form-switch" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="has_time_limit_switch"
                  name="has_time_limit"
                  checked={formData.has_time_limit}
                  onChange={handleChange}
                  style={{ width: '42px', height: '22px', cursor: 'pointer', margin: 0 }}
                />
                <label htmlFor="has_time_limit_switch" style={{ margin: 0, fontWeight: '700', fontSize: '13px', color: formData.has_time_limit ? '#9333ea' : '#64748b', cursor: 'pointer' }}>
                  {formData.has_time_limit ? 'Schedule Enabled' : 'Always Active (No Limit)'}
                </label>
              </div>
            </div>

            {formData.has_time_limit && (
              <div style={{ padding: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                      <FaCalendarAlt style={{ color: '#9333ea' }} /> Start Date
                    </label>
                    <input
                      type="date"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleChange}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                    />
                  </div>

                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                      <FaCalendarAlt style={{ color: '#9333ea' }} /> End Date
                    </label>
                    <input
                      type="date"
                      name="end_date"
                      value={formData.end_date}
                      onChange={handleChange}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                    />
                  </div>

                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                      <FaClock style={{ color: '#9333ea' }} /> Daily Start Time
                    </label>
                    <input
                      type="time"
                      name="start_time"
                      value={formData.start_time}
                      onChange={handleChange}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                    />
                  </div>

                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                      <FaClock style={{ color: '#9333ea' }} /> Daily End Time
                    </label>
                    <input
                      type="time"
                      name="end_time"
                      value={formData.end_time}
                      onChange={handleChange}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '8px', display: 'block' }}>
                    Active Days of the Week
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => {
                      const isSelected = formData.days_of_week?.includes(day);
                      return (
                        <button
                          key={day}
                          type="button"
                          onClick={() => {
                            setFormData(prev => {
                              const currentDays = prev.days_of_week || [];
                              const updated = isSelected 
                                ? currentDays.filter(d => d !== day)
                                : [...currentDays, day];
                              return { ...prev, days_of_week: updated };
                            });
                          }}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '20px',
                            border: isSelected ? '1.5px solid #9333ea' : '1.5px solid #e2e8f0',
                            background: isSelected ? '#f3e8ff' : '#f8fafc',
                            color: isSelected ? '#7e22ce' : '#64748b',
                            fontWeight: isSelected ? '700' : '500',
                            fontSize: '13px',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          {isSelected ? '✓ ' : ''}{day}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showAssignmentModal && (
        <div className="add-modal-overlay">
          <div className="add-modal" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <span>Reward Rule Assignments</span>
              <button className="close-modal-btn" onClick={() => setShowAssignmentModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}><FaTimes /></button>
            </div>
            <div className="modal-body" style={{ marginTop: '20px' }}>
              {showCompanyAssignBtn && (
                <div className="assignment-card" style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: '#3b82f6', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px', fontWeight: '700', marginBottom: '15px' }}><FaUsers /> Select Company *</h4>
                  <div className="checkbox-group" style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                    {companyOptions.map((comp, idx) => (
                      <label key={idx} className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', background: '#eff6ff', borderRadius: '12px', border: '1.5px solid #dbeafe', cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#1e40af', transition: 'all 0.2s ease' }}>
                        <input type="checkbox" checked={selectedCompanies.includes(comp)} onChange={(e) => { setSelectedCompanies(prev => e.target.checked ? [...prev, comp] : prev.filter(c => c !== comp)); }} style={{ width: '18px', height: '18px', accentColor: '#3b82f6', cursor: 'pointer' }} />
                        {comp}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {showBranchAssignBtn && (
                <div className="assignment-card" style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px', fontWeight: '700', marginBottom: '15px' }}><FaMapMarkerAlt /> Select Branch (Optional)</h4>
                  <div className="checkbox-group" style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                    {availableBranches.map((branch, idx) => (
                      <label key={idx} className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', background: '#ecfdf5', borderRadius: '12px', border: '1.5px solid #d1fae5', cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#065f46', transition: 'all 0.2s ease' }}>
                        <input type="checkbox" checked={selectedBranches.includes(branch)} onChange={(e) => { setSelectedBranches(prev => e.target.checked ? [...prev, branch] : prev.filter(b => b !== branch)); }} style={{ width: '18px', height: '18px', accentColor: '#10b981', cursor: 'pointer' }} />
                        {branch}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="save-btn" onClick={handleSubmit} disabled={loading || selectedCompanies.length === 0}>
                {loading ? "Saving..." : "Confirm & Save"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CreateRewardPage;
